/**
 * ============================================================
 *  KARPUNG — API Client (terpusat, type-safe, satu sumber)
 *
 *  Menggantikan fetch() manual yang tersebar → sumber bug utama
 *  di v1 (path/param/header salah). Semua panggilan API lewat sini.
 *
 *  Fitur otomatis:
 *   - Base URL dari VITE_API_URL (prod) / proxy Vite (dev)
 *   - Param ?tenant=<subdomain> otomatis (isolasi tenant)
 *   - Header Authorization: Bearer <token> otomatis
 *   - Error konsisten (lempar Error dgn pesan dari server)
 *
 *  v2 — PER-TENANT AUTH:
 *   - Token disimpan per-tenant: karpung_session_{tenantId}
 *   - Setiap request otomatis pakai token tenant yg aktif
 *   - Backward compat: legacy key "karpung_session" tetap di-set
 *     agar file lama yg baca localStorage langsung tetap jalan
 * ============================================================
 */

import { detectTenant as detectTenantCore } from "@/lib/utils";

const API_BASE = (import.meta.env.VITE_API_URL || "") + "/api";

/* CDN publik (custom domain R2) untuk menampilkan foto/video */
export const IMG_CDN = "https://img.karpung.com";
export const VIDEO_CDN = "https://cdn.karpung.com";

/* ── Token key per-tenant ── */
const LEGACY_TOKEN_KEY = "karpung_session";

function tokenKey(tenantId: string): string {
  return `karpung_session_${tenantId}`;
}

/** Baca token untuk tenant tertentu */
export function getTenantToken(tenantId: string): string | null {
  try {
    return localStorage.getItem(tokenKey(tenantId))
      ?? localStorage.getItem(LEGACY_TOKEN_KEY); // fallback saat migrasi
  } catch {
    return null;
  }
}

/** Simpan token untuk tenant tertentu */
export function setTenantToken(tenantId: string, token: string): void {
  try {
    localStorage.setItem(tokenKey(tenantId), token);
    // Backward compat: legacy key agar file yg baca langsung tetap jalan
    localStorage.setItem(LEGACY_TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
}

/** Hapus token untuk tenant tertentu */
export function clearTenantToken(tenantId: string): void {
  try {
    localStorage.removeItem(tokenKey(tenantId));
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/** Hapus SEMUA token dari seluruh tenant (full logout) */
export function clearAllTokens(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith("karpung_session_") || key === LEGACY_TOKEN_KEY)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

/* ── BACKWARD COMPAT: API lama tetap jalan (pakai tenant saat ini) ── */
export function getToken(): string | null {
  return getTenantToken(detectTenantCore("adara"));
}
export function setToken(token: string): void {
  setTenantToken(detectTenantCore("adara"), token);
}
export function clearToken(): void {
  clearTenantToken(detectTenantCore("adara"));
}

/* ── User cache key per-tenant (dipakai AuthProvider) ── */
const LEGACY_USER_KEY = "karpung_user";

export function getUserCacheKey(tenantId: string): string {
  return `karpung_user_${tenantId}`;
}

export function getLegacyUserKey(): string {
  return LEGACY_USER_KEY;
}

/**
 * Re-export canonical detectTenant with "adara" as the SSR/localhost default
 * (backward-compatible with callers that relied on the old default).
 */
export const detectTenant = () => detectTenantCore("adara");

/* ── URL media R2 (via CDN publik) ── */
export function photoUrl(r2Key: string, opts?: { w?: number }): string {
  if (!r2Key) return "";
  if (r2Key.startsWith("http")) {
    if (opts?.w) return `${r2Key}?w=${opts.w}`;
    return r2Key;
  }
  const qs = opts?.w ? `?w=${opts.w}` : "";
  return `${IMG_CDN}/${r2Key}${qs}`;
}

/* URL thumbnail untuk grid (400px wide) */
export function thumbUrl(r2Key: string): string {
  return photoUrl(r2Key, { w: 400 });
}
export function videoUrl(r2Key: string): string {
  if (!r2Key) return "";
  if (r2Key.startsWith("http")) return r2Key;
  return `${VIDEO_CDN}/${r2Key}`;
}

/* ── Inti request ── */
interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  auth?: boolean;
  signal?: AbortSignal;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, auth = false, signal } = opts;

  const tenantId = detectTenantCore("adara");
  const sep = path.includes("?") ? "&" : "?";
  const ts = Date.now();
  const url = `${API_BASE}${path}${sep}tenant=${encodeURIComponent(tenantId)}&_ts=${ts}`;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (auth) {
    const token = getTenantToken(tenantId);
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new Error((json.error as string) || `HTTP ${res.status}`);
  }
  return json as T;
}

/* Upload file (FormData) — tanpa Content-Type manual */
export async function upload<T>(path: string, form: FormData): Promise<T> {
  const tenantId = detectTenantCore("adara");
  const sep = path.includes("?") ? "&" : "?";
  const ts = Date.now();
  const url = `${API_BASE}${path}${sep}tenant=${encodeURIComponent(tenantId)}&_ts=${ts}`;
  const headers: Record<string, string> = {};
  const token = getTenantToken(tenantId);
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(url, { method: "POST", headers, body: form });
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new Error((json.error as string) || `HTTP ${res.status}`);
  return json as T;
}

/* ============================================================
 *  Tipe respons
 * ============================================================ */
export interface AuthUser {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: "CUSTOMER" | "ADMIN" | "SUPER_ADMIN";
  sessionToken?: string;
}

export interface PortfolioPhotoRow {
  id: string;
  album_id: string;
  tenant_id: string;
  r2_key: string;
  label: string;
  sort_order: number;
  size_bytes: number;
}
export interface PortfolioAlbumRow {
  id: string;
  tenant_id: string;
  client_name: string;
  category: string;
  cover_image_key: string;
  sort_order: number;
  photos: PortfolioPhotoRow[];
}

/* ── Bookings ── */
export interface BookingUI {
  id: string;
  packageName: string;
  date: string;
  timeSlot: string;
  totalPrice: number;
  statusPembayaran: string;
  statusPengerjaan: string;
  addOns: string[];
  paymentType: "full" | "dp";
  remainingPayment: number;
  dpAmount: number;
  voucherCode: string;
  discountAmount: number;
  clientName: string;
  phone: string;
}

export interface BookingInput {
  serviceId: string;
  packageName: string;
  date: string;
  timeSlot: string;
  totalPrice: number;
  statusPembayaran: string;
  statusPengerjaan: string;
  addOns: string[];
  paymentType: "full" | "dp";
  remainingPayment: number;
  dpAmount: number;
  voucherCode: string;
  discountAmount: number;
  clientName: string;
  phone: string;
}

/* Ubah baris D1 (snake_case) → bentuk UI (camelCase) */
export function mapBooking(row: Record<string, unknown>): BookingUI {
  let addOns: string[] = [];
  try {
    addOns = JSON.parse(String(row.add_ons ?? "[]"));
  } catch {
    addOns = [];
  }
  return {
    id: String(row.id),
    packageName: String(row.package_name ?? ""),
    date: String(row.date ?? ""),
    timeSlot: String(row.time_slot ?? ""),
    totalPrice: Number(row.total_price ?? 0),
    statusPembayaran: String(row.status_pembayaran ?? "PENDING"),
    statusPengerjaan: String(row.status_pengerjaan ?? "SESSION_DONE"),
    addOns,
    paymentType: row.payment_type === "dp" ? "dp" : "full",
    remainingPayment: Number(row.remaining_payment ?? 0),
    dpAmount: Number(row.dp_amount ?? 0),
    voucherCode: String(row.voucher_code ?? ""),
    discountAmount: Number(row.discount_amount ?? 0),
    clientName: String(row.client_name ?? ""),
    phone: String(row.phone ?? ""),
  };
}

/* ── Vouchers ── */
export interface VoucherValid {
  code: string;
  type: string;
  value: number;
  minPurchase: number;
  description: string;
}

/* ============================================================
 *  Endpoints
 * ============================================================ */
export const api = {
  /* Tenant */
  tenant: () => request<{ data: unknown }>("/tenant"),

  /* Auth — v2: login/register menyertakan tenant_id */
  auth: {
    login: (email: string, password: string) =>
      request<{ data: AuthUser }>("/auth/login", {
        method: "POST",
        body: { email, password, tenant_id: detectTenantCore("adara") },
      }),
    register: (data: { name: string; email: string; phone?: string; password: string }) =>
      request<{ data: { id: string; name: string; email: string } }>("/auth/register", {
        method: "POST",
        body: { ...data, tenant_id: detectTenantCore("adara") },
      }),
    session: () => request<{ data: AuthUser }>("/auth/session", { auth: true }),
    logout: () => request<{ success: boolean }>("/auth/logout", { method: "POST", auth: true }),
  },

  /* Portfolio (admin) */
  portfolio: {
    albums: () => request<{ data: PortfolioAlbumRow[] }>("/portfolio/albums"),  // public read-only
    createAlbum: (clientName: string, category: string) =>
      request<{ data: { id: string } }>("/admin/portfolio/albums", {
        method: "POST",
        auth: true,
        body: { clientName, category },
      }),
    updateAlbum: (id: string, data: { clientName?: string; category?: string }) =>
      request<{ success: boolean }>(`/admin/portfolio/albums/${id}`, { method: "PUT", auth: true, body: data }),
    deleteAlbum: (id: string) =>
      request<{ success: boolean }>(`/admin/portfolio/albums/${id}`, { method: "DELETE", auth: true }),
    uploadPhoto: (albumId: string, file: File) => {
      const form = new FormData();
      form.append("file", file);
      return upload<{ data: { id: string; key: string } }>(`/admin/portfolio/albums/${albumId}/photos`, form);
    },
    uploadCover: (albumId: string, file: File) => {
      const form = new FormData();
      form.append("file", file);
      return upload<{ data: { key: string } }>(`/admin/portfolio/albums/${albumId}/cover`, form);
    },
    deletePhoto: (id: string) =>
      request<{ success: boolean }>(`/admin/portfolio/photos/${id}`, { method: "DELETE", auth: true }),

    /* Videos */
    uploadVideo: (albumId: string, title: string, file: File, category?: string, duration?: string, description?: string) => {
      const form = new FormData();
      form.append("file", file);
      form.append("title", title);
      form.append("albumId", albumId);
      if (category) form.append("category", category);
      if (duration) form.append("duration", duration);
      if (description) form.append("description", description);
      return upload<{ data: { id: string; key: string } }>("/admin/portfolio/videos", form);
    },
    listVideos: () => request<{ data: Record<string, unknown>[] }>("/portfolio/videos"),  // public read-only
    deleteVideo: (id: string) => request<{ success: boolean }>(`/admin/portfolio/videos/${id}`, { method: "DELETE", auth: true }),
  },

  /* Storage */
  storage: () => request<{ data: unknown }>("/admin/storage", { auth: true }),

  /* Services (publik) */
  services: {
    list: () => request<{ data: unknown[] }>("/services"),
  },

  /* Bookings */
  bookings: {
    slots: (date: string) =>
      request<{ data: { date: string; available: string[]; booked: string[] } }>(
        `/bookings/slots?date=${encodeURIComponent(date)}`,
      ),
    mine: async (): Promise<BookingUI[]> => {
      const { data } = await request<{ data: Record<string, unknown>[] }>("/bookings", { auth: true });
      return data.map(mapBooking);
    },
    create: (input: BookingInput) =>
      request<{ data: { id: string } }>("/bookings", { method: "POST", auth: true, body: input }),
  },

  /* Vouchers */
  vouchers: {
    validate: (code: string) =>
      request<{ data: VoucherValid }>(`/vouchers?code=${encodeURIComponent(code)}`),
    list: () => request<{ data: Record<string, unknown>[] }>("/vouchers/list", { auth: true }),
    create: (input: {
      code: string;
      type: "PERCENTAGE" | "NOMINAL";
      value: number;
      quota?: number;
      minPurchase?: number;
      expiredAt: string;
      description?: string;
    }) => request<{ data: { id: string } }>("/vouchers", { method: "POST", auth: true, body: input }),
  },

  /* Admin (bookings) */
  admin: {
    bookings: async (): Promise<BookingUI[]> => {
      const { data } = await request<{ data: Record<string, unknown>[] }>("/admin/bookings", { auth: true });
      return data.map(mapBooking);
    },
    updateBooking: (id: string, data: { statusPembayaran?: string; statusPengerjaan?: string }) =>
      request<{ success: boolean }>(`/admin/bookings/${id}`, { method: "PUT", auth: true, body: data }),
  },

  /* Super admin */
  super: {
    tenants: () => request<{ data: unknown[] }>("/super/tenants", { auth: true }),
    stats: () => request<{ data: unknown }>("/super/stats", { auth: true }),
  },
};
